import { CookieMixins } from '@/mixins/cookie';
import OrderService from '@/api/services/OrderService';

const { methods: Cookie } = CookieMixins;

// Held outside Vuex state: a Promise is not serializable and must not be reactive. Keyed by
// item so a dispatch for a DIFFERENT item never receives another item's result — the caller
// post-processes acknowledgement markers off this value and cannot tell whose order it is.
let changeBillingIntervalInFlight = null;
let changeBillingIntervalInFlightKey = null;

export const namespaced = true;

export const state = {
  order: {},
  loading: false,
  discount: {
    loading: false,
    success: '',
    error: '',
  },
  donation: {
    loading: false,
    success: '',
    error: '',
  },
  pay: {
    loading: false,
    error: '',
  },
  removeItem: {
    loading: false,
    error: false,
    itemId: null,
  },
  addItem: {
    loading: false,
    error: false,
    itemId: null,
  },
  changeBillingInterval: {
    loading: false,
    error: false,
    itemId: null,
  },
  prepareOrder: {
    loading: false,
    message: '',
  },
};

export const mutations = {
  SET_ORDER(state, order) {
    state.order = order;
    window.AnalyticsFunnels?.onOrderLoaded(order);
  },
  LOADING(state, loading) {
    state.loading = loading;
  },
  DISCOUNTLOADING(state, loading) {
    state.discount.loading = loading;
  },
  DISCOUNTSUCCESS(state, message) {
    state.discount.success = message;
  },
  DISCOUNTERROR(state, message) {
    state.discount.error = message;
  },
  DONATIONLOADING(state, loading) {
    state.donation.loading = loading;
  },
  DONATIONSUCCESS(state, message) {
    state.donation.success = message;
  },
  DONATIONERROR(state, message) {
    state.donation.error = message;
  },
  PAYLOADING(state, loading) {
    state.pay.loading = loading;
  },
  PAYERROR(state, error) {
    state.pay.error = error;
  },
  REMOVEITEMLOADING(state, loading) {
    state.removeItem.loading = loading;
  },
  REMOVEITEMERROR(state, error) {
    state.removeItem.error = error;
  },
  REMOVEITEMID(state, itemId) {
    state.removeItem.itemId = itemId;
  },
  ADDITEMLOADING(state, loading) {
    state.addItem.loading = loading;
  },
  ADDITEMERROR(state, error) {
    state.addItem.error = error;
  },
  ADDITEMID(state, itemId) {
    state.addItem.itemId = itemId;
  },
  CHANGEBILLINGINTERVALLOADING(state, loading) {
    state.changeBillingInterval.loading = loading;
  },
  CHANGEBILLINGINTERVALERROR(state, error) {
    state.changeBillingInterval.error = error;
  },
  CHANGEBILLINGINTERVALID(state, itemId) {
    state.changeBillingInterval.itemId = itemId;
  },
  PREPARE_ORDER_LOADING(state, loading) {
    state.prepareOrder.loading = loading;
  },
  PREPARE_ORDER_MESSAGE(state, message) {
    state.prepareOrder.message = message;
  },
  async STORE_LOCAL_ITEMS(state, items = []) {
    Cookie.setBasketCookie([...(await Cookie.splitSetBasket()), ...items]);
  },
};

export const actions = {
  fetchOrder({ commit, dispatch }, id) {
    commit('LOADING', true);
    OrderService.getOrder(id)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('LOADING', false);
      })
      .catch((error) => {
        commit('LOADING', false);
        const notification = {
          type: 'warning',
          message: error.message,
        };
        dispatch('notification/add', notification, { root: true });
      });
  },
  createLocalOrder({ commit, dispatch }, { cityId, data, discountCode, changingDiscount }) {
    changingDiscount ? commit('DISCOUNTLOADING', true) : commit('LOADING', true);
    let products = [];
    data?.forEach((productDetails) => {
      products.push({
        productId: productDetails.productId,
        children: productDetails.connector
          ? [
              {
                productId: productDetails.connector,
              },
              ...productDetails.otherProductIds
                .filter((productId) => productId)
                .map((productId) => ({
                  productId,
                })),
            ]
          : null,
        petId: null,
        personalization: productDetails.personalize?.selected
          ? { name: productDetails.personalize?.name, phone: productDetails.personalize?.phone }
          : null,
        designExports: productDetails.designExports
          ? { tagFront: productDetails.designExports?.tagFront, laserFront: productDetails.designExports?.laserFront }
          : null,
        keyChain: productDetails.keyChain,
      });
    });
    return new Promise((resolve, reject) =>
      OrderService.temporaryOrder(cityId, products, discountCode)
        .then((response) => {
          commit('SET_ORDER', response);
          changingDiscount ? commit('DISCOUNTSUCCESS', discountCode ? 'Successfully Applied.' : 'Successfully Removed.') : null;
          resolve(response);
        })
        .catch((error) => {
          if (changingDiscount) {
            commit('DISCOUNTSUCCESS', '');
            if (error.message && error.message.length && error.message[0].message) {
              commit('DISCOUNTERROR', error.message[0].message);
            } else {
              commit('DISCOUNTERROR', 'Uhoh! Something went wrong.');
            }
          } else {
            const notification = {
              type: 'warning',
              message: error.message,
            };
            dispatch('notification/add', notification, { root: true });
          }

          reject(error);
        })
        .finally(() => (changingDiscount ? commit('DISCOUNTLOADING', false) : commit('LOADING', false)))
    );
  },
  applyDiscount({ commit }, { orderId, code }) {
    commit('DISCOUNTLOADING', true);
    commit('DISCOUNTSUCCESS', 'Applying Discount...');
    return OrderService.applyDiscount(orderId, code)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('DISCOUNTLOADING', false);
        commit('DISCOUNTERROR', '');
        commit('DISCOUNTSUCCESS', response?.discountAppliedMessage || 'Successfully Applied.');
        return Promise.resolve();
      })
      .catch((error) => {
        commit('DISCOUNTLOADING', false);
        commit('DISCOUNTSUCCESS', '');
        if (error.message && error.message.length && error.message[0].message) {
          commit('DISCOUNTERROR', error.message[0].message);
        } else {
          commit('DISCOUNTERROR', 'Uhoh! Something went wrong.');
        }
        return Promise.resolve();
      });
  },
  removeDiscount({ commit, state }) {
    commit('DISCOUNTLOADING', true);
    OrderService.removeDiscount(state.order.id)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('DISCOUNTLOADING', false);
        commit('DISCOUNTERROR', '');
        commit('DISCOUNTSUCCESS', 'Successfully Removed.');
      })
      .catch((error) => {
        commit('DISCOUNTLOADING', false);
        commit('DISCOUNTSUCCESS', '');
        console.log(error);
        if (error.message && error.message.length && error.message[0].message) {
          commit('DISCOUNTERROR', error.message[0].message);
        } else {
          commit('DISCOUNTERROR', 'Uhoh! Something went wrong.');
        }
      });
  },
  orderDonation({ commit, state }, { amount, push }) {
    commit('DONATIONLOADING', true);
    OrderService.orderDonation(state.order.id, amount)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('DONATIONLOADING', false);
        commit('DONATIONERROR', '');
        commit('DONATIONSUCCESS', 'views.payment.donationSuccess');
      })
      .catch((error) => {
        commit('DONATIONLOADING', false);
        commit('DONATIONSUCCESS', '');
        console.log(error);
        if (error.message && error.message.length && error.message[0].message) {
          commit('DISCOUNTERROR', error.message[0].message);
        } else {
          commit('DISCOUNTERROR', 'Uhoh! Something went wrong.');
        }
      })
      .finally(() => {
        push();
      });
  },
  removeDonationSuccess({ commit }) {
    commit('DONATIONSUCCESS', '');
  },
  pay({ commit, state }, payload) {
    const { paymentMethod, setDefaultPaymentMethod, autoRenewalConfig, marketingCommunicationOptIn } = payload;

    commit('PAYLOADING', true);
    commit('PAYERROR', '');

    return new Promise((resolve, reject) =>
      OrderService.pay(
        state.order.id,
        paymentMethod,
        state.order.total,
        setDefaultPaymentMethod,
        autoRenewalConfig,
        marketingCommunicationOptIn
      )
        .then(resolve)
        .catch((error) => {
          if (error.message && error.message[0]) {
            commit('PAYERROR', error.message[0].message);
          }
          console.error(error);
          reject();
        })
        .finally(() => commit('PAYLOADING', false))
    );
  },
  removeItem({ commit, state, dispatch }, { itemId }) {
    commit('REMOVEITEMLOADING', true);
    commit('REMOVEITEMID', itemId);
    OrderService.removeItem(state.order.id, itemId)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('REMOVEITEMLOADING', false);
        commit('REMOVEITEMID', null);
        commit('REMOVEITEMERROR', false);
      })
      .catch((error) => {
        commit('REMOVEITEMLOADING', false);
        commit('REMOVEITEMERROR', true);
        console.log(error);
        commit('LOADING', false);
        const notification = {
          type: 'warning',
          message: 'There was a problem removing your item',
        };
        dispatch('notification/add', notification, { root: true });
      });
  },
  changeMembershipBillingInterval({ commit, state, dispatch }, { itemId, billingInterval }) {
    const key = `${itemId}:${billingInterval}`;

    // Same item and same target: hand back the in-flight promise so the caller awaits the real
    // result. Any other request while one is in flight resolves null, which the caller already
    // treats as "did not happen" — never another item's order, which it would read as success.
    if (changeBillingIntervalInFlight) {
      return changeBillingIntervalInFlightKey === key ? changeBillingIntervalInFlight : Promise.resolve(null);
    }

    changeBillingIntervalInFlightKey = key;
    commit('CHANGEBILLINGINTERVALLOADING', true);
    commit('CHANGEBILLINGINTERVALID', itemId);
    // Clear the previous banner up front, otherwise a retry shows the old error while loading.
    commit('CHANGEBILLINGINTERVALERROR', false);

    changeBillingIntervalInFlight = OrderService.changeMembershipBillingInterval(state.order.id, itemId, billingInterval)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('CHANGEBILLINGINTERVALLOADING', false);
        commit('CHANGEBILLINGINTERVALID', null);
        commit('CHANGEBILLINGINTERVALERROR', false);

        return response;
      })
      .catch((error) => {
        commit('CHANGEBILLINGINTERVALLOADING', false);
        // Keep the item ID: Product.vue gates the inline error on
        // `error && itemId === item.id`, so nulling it here made the banner unreachable.
        commit('CHANGEBILLINGINTERVALERROR', true);
        console.log(error);

        const notification = {
          type: 'warning',
          message: 'There was a problem changing the billing frequency of your membership',
        };
        dispatch('notification/add', notification, { root: true });

        return null;
      })
      .finally(() => {
        changeBillingIntervalInFlight = null;
        changeBillingIntervalInFlightKey = null;
      });

    return changeBillingIntervalInFlight;
  },
  removePersonalization({ commit, state, dispatch }, { itemId }) {
    commit('REMOVEITEMLOADING', true);
    commit('REMOVEITEMID', itemId);
    OrderService.removePersonalization(state.order.id, itemId)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('REMOVEITEMLOADING', false);
        commit('REMOVEITEMERROR', false);
        commit('REMOVEITEMID', null);
      })
      .catch((error) => {
        commit('REMOVEITEMLOADING', false);
        commit('REMOVEITEMERROR', true);
        console.log(error);
        commit('LOADING', false);
        const notification = {
          type: 'warning',
          message: 'There was a problem removing your item',
        };
        dispatch('notification/add', notification, { root: true });
      });
  },
  addItemOrPersonalization(
    { commit, dispatch, rootState },
    { vm, orderId, itemId, productDetails, personalization, data, discountCode, changingDiscount }
  ) {
    commit('ADDITEMLOADING', true);
    commit('ADDITEMID', itemId || productDetails?.parentId);
    const dispatchAction = () =>
      new Promise((resolve, reject) => {
        rootState?.orderId || orderId
          ? OrderService[!personalization ? 'addItem' : 'addPersonalization']?.(
              orderId,
              !personalization ? productDetails : { personalization, itemId }
            )
              .then(resolve)
              .catch(reject)
          : dispatch('createLocalOrder', { cityId: vm.cityId, data, discountCode, changingDiscount }).then(resolve).catch(reject);
      });
    return new Promise((resolve, reject) =>
      dispatchAction()
        .then((response) => {
          commit('SET_ORDER', response);
          commit('ADDITEMLOADING', false);
          commit('ADDITEMID', null);
          commit('ADDITEMERROR', false);
          resolve(response);
        })
        .catch((error) => {
          commit('ADDITEMLOADING', false);
          commit('ADDITEMERROR', error?.message?.[0]?.message);
          console.log(error);
          commit('LOADING', false);
          const notification = {
            type: 'warning',
            message: 'There was a problem adding your item',
          };
          dispatch('notification/add', notification, { root: true });
          reject(error);
        })
    );
  },
  cancelOrder({ commit, dispatch }, orderId) {
    commit('LOADING', true);
    return OrderService.cancelOrder(orderId)
      .then((response) => {
        commit('SET_ORDER', response);
      })
      .catch((error) => {
        error.location = 'cancelOrder';
        const notification = {
          type: 'warning',
          message: error.message,
        };
        dispatch('notification/add', notification, { root: true });

        return Promise.reject();
      })
      .finally(() => commit('LOADING', false));
  },
  upgradeToPriorityShipping: ({ commit, dispatch }, orderId) =>
    new Promise((resolve, reject) => {
      commit('LOADING', true);
      OrderService.upgradeToPriorityShipping(orderId)
        .then((response) => {
          commit('SET_ORDER', response);
          dispatch('notification/success', { message: 'Successfully upgraded to Priority & Tracked Shipping.' }, { root: true });
          resolve();
        })
        .catch((error) => {
          error.location = 'orderShipping';
          console.log(error);
          dispatch(
            'notification/add',
            { type: 'warning', message: 'Failed to upgrade to priority & tracked shipping. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => {
          commit('LOADING', false);
        });
    }),
  downgradeToRegularShipping: ({ commit, dispatch }, orderId) =>
    new Promise((resolve, reject) => {
      commit('LOADING', true);
      OrderService.downgradeToRegularShipping(orderId)
        .then((response) => {
          commit('SET_ORDER', response);
          dispatch(
            'notification/success',
            { message: 'Successfully downgraded to Regular Mail Shipping (Free).', options: { dismissSecs: 15 } },
            { root: true }
          );
          resolve();
        })
        .catch((error) => {
          error.location = 'orderShipping';
          dispatch(
            'notification/add',
            { type: 'warning', message: 'Failed to downgrade to Regular Mail Shipping. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => {
          commit('LOADING', false);
        });
    }),
  removeItemsByProduct({ commit, state, dispatch }, { itemId, productId, quantity }) {
    commit('REMOVEITEMLOADING', true);
    commit('REMOVEITEMID', itemId);
    OrderService.removeItemsByProduct(state.order.id, productId, quantity)
      .then((response) => {
        commit('SET_ORDER', response);
        commit('REMOVEITEMLOADING', false);
        commit('REMOVEITEMID', null);
        commit('REMOVEITEMERROR', false);
      })
      .catch((error) => {
        commit('REMOVEITEMLOADING', false);
        commit('REMOVEITEMERROR', true);
        console.error(error);
        commit('LOADING', false);
        const notification = {
          type: 'warning',
          message: 'There was a problem removing your items',
        };
        dispatch('notification/add', notification, { root: true });
      });
  },
  async addLocalItem({ commit, dispatch, rootState }, { data, quantity = 1 }) {
    commit('ADDITEMLOADING', true);
    commit('ADDITEMID', null);
    for (let i = 0; i < quantity; i++) {
      await commit('STORE_LOCAL_ITEMS', data);
    }
    return new Promise((resolve, reject) =>
      dispatch('createLocalOrder', {
        cityId: rootState.cityId,
        data: Cookie.splitSetBasket(),
        discountCode: Cookie.getBasketCodeCookie(),
        changingDiscount: false,
      })
        .then((response) => {
          commit('SET_ORDER', response);
          commit('ADDITEMERROR', false);
          resolve(response);
        })
        .catch((error) => {
          commit('ADDITEMERROR', error?.message?.[0]?.message);
          console.log(error);
          const notification = {
            type: 'warning',
            message: 'There was a problem adding your item',
          };
          dispatch('notification/add', notification, { root: true });
          reject(error);
        })
        .finally(() => {
          commit('ADDITEMLOADING', false);
          commit('ADDITEMID', null);
        })
    );
  },
  removeWalletCard({ commit, dispatch }, input) {
    commit('REMOVEITEMLOADING', true);
    commit('REMOVEITEMID', input.petId);
    return new Promise((resolve, reject) =>
      OrderService.removeWalletCard(input)
        .then((response) => {
          commit('SET_ORDER', response);
          commit('REMOVEITEMID', null);
          commit('REMOVEITEMERROR', false);
          resolve(response);
        })
        .catch((error) => {
          commit('REMOVEITEMERROR', true);
          const notification = {
            type: 'warning',
            message: error.message,
          };
          dispatch('notification/add', notification, { root: true });
          reject(error);
        })
        .finally(() => commit('REMOVEITEMLOADING', false))
    );
  },
  async prepareOrderForPayment({ commit, dispatch }, input) {
    try {
      commit('PREPARE_ORDER_LOADING', true);
      commit('PREPARE_ORDER_MESSAGE', null);
      const results = await OrderService.prepareOrderForPayment(input);

      commit('SET_ORDER', results);

      if (results?.prepareOrderForPaymentContext) {
        commit('PREPARE_ORDER_MESSAGE', results.prepareOrderForPaymentContext.removedItems);
      }
    } catch (error) {
      dispatch('notification/add', { type: 'warning', message: error.message, options: { dismissSecs: 15 } }, { root: true });
    } finally {
      commit('PREPARE_ORDER_LOADING', false);
    }
  },
};

export const getters = {
  getPets: (state) => {
    let pets = [];
    if (state.order?.id) {
      state.order?.petOwner?.pets?.forEach((pet) => {
        if (
          state.order?.items?.find(
            (item) =>
              item?.group?.pet?.id == pet.id && !item?.product?.name?.includes('Voice & Sight') && !item?.product?.name?.includes('V&S')
          )
        ) {
          pets.push(pet);
        }
      });
    }
    return pets;
  },
  getSections: (state, getters) => {
    let sections = {
      pets: {},
      donations: [],
      additional: [],
      essentials: [],
      miscellaneous: [],
      walletCardConfigurations: [],
      vs: {
        pet: [],
        petOwner: [],
      },
    };

    if (state.order?.id) {
      let seen = [];

      getters.getPets.forEach((pet) => {
        sections.pets[pet.id] = {
          licenses: [],
          replacements: [],
          tags: [],
          collars: [],
          walletcards: [],
          petTrackingDevices: [],
          subscriptions: []
        };
      });

      state.order?.items?.forEach((item) => {
        if (!seen.includes(item.id)) {
          seen.push(item.id);
          if (item.product && ['WALLET_CARD'].includes(item.product.type) && item?.group?.pet?.id) {
            if (item.children?.length >= 0 && !item.parent) {
              sections.pets[item.group.pet.id]?.walletcards.push(item);
            }
          } else if (item.product && ['SUBSCRIPTION'].includes(item.product.type)) {
              if (item.product.productSubType === 'MEMBERSHIP') {
                sections.pets[item.group.pet.id]?.subscriptions.push(item);
              }
            // Javascript doesn't have a `break;` or `continue;`
            // The subscription is handled as a child of the Pet Tracking Device.
            // We do not want to include this product as a standalone order item
            // else we will be displaying duplicates in the UI.
          } else if (item.product && ['PET_TRACKING_DEVICE'].includes(item.product.type)) {
            sections.pets[item.group.pet.id]?.petTrackingDevices.push(item);
          } else if (item.product.name.includes('Voice & Sight') || item.product.name.includes('V&S')) {
            if (['PET_TAG', 'REPLACEMENT_TAG'].includes(item.product.type)) {
              sections.vs.pet.push(item);
            } else if (item.product.type === 'VOICE_AND_SIGHT') {
              sections.vs.petOwner.push(item);
            }
          } else if (item.product && ['LICENCE', 'LATE_FEE'].includes(item.product.type) && item?.group?.pet?.id) {
            sections.pets[item.group.pet.id].licenses.push(item);
          } else if (item.product && ['PET_TAG', 'DESIGNER_TAG'].includes(item.product.type) && item?.group?.pet?.id) {
            sections.pets[item.group.pet.id]?.tags.push(item);
            item.children.forEach((child) => {
              seen.push(child.id);
            });
          } else if (item.product && ['COLLAR'].includes(item.product.type) && item?.group?.pet?.id) {
            sections.pets[item.group.pet.id]?.collars.push(item);
          } else if (item.product && item.product.type === 'DONATION') {
            sections.donations.push(item);
          } else if (item.product && item.product.type === 'REPLACEMENT_TAG' && item?.group?.pet?.id) {
            sections.pets[item.group.pet.id].replacements.push(item);
          } else if (item.product && item.product?.type === 'PET_ESSENTIALS' && !item.parent) {
            // COMBINE QUANTITY
            const existingItemIndex = sections.essentials.findIndex((prod) => prod.product.id === item.product.id);
            if (existingItemIndex !== -1 && item.product?.type === 'PET_ESSENTIALS') {
              sections.essentials[existingItemIndex].product.quantity++;
              sections.essentials[existingItemIndex].product.totalQtyPrice += item.product.price;
              sections.essentials[existingItemIndex].product.totalQtyDiscountedPrice += Number(item.subtotal - item.discount);
            } else {
              item.product.quantity = 1;
              item.product.totalQtyPrice = item.product.price;
              item.product.totalQtyDiscountedPrice = Number(item.subtotal - item.discount);
              sections.essentials.push(item);
            }
          } else if (item.product && item.product.type === 'ADDITIONAL_FEE') {
            if (item.product?.subType === 'pet' && item.group?.pet?.id) {
              sections.pets[item.group.pet.id].licenses.push(item);
            } else {
              item.product.quantity = 1;
              item.product.totalQtyPrice = item.product.price;
              item.product.totalQtyDiscountedPrice = Number(item.subtotal - item.discount);
              sections.additional.push(item);
            }
          } else {
            if (item.parent) {
              return;
            }
            item.product.quantity = 1;
            item.product.totalQtyPrice = item.product.price;
            item.product.totalQtyDiscountedPrice = Number(item.subtotal - item.discount);
            sections.miscellaneous.push(item);
          }
        }
      });

      // Add wallet card configurations for groups without pets
      state.order.groups
      ?.filter((group) => !group.pet && group.walletCardConfigurations)
      ?.forEach((group) => sections.walletCardConfigurations.push(...group.walletCardConfigurations?.filter((config) => config?.optedIn)));
    } else if (state.order) {
      state.order?.items?.forEach((item) => {
        if (!item.parent) {
          if (item.product.type !== 'PET_ESSENTIALS') {
            if (['COLLAR', 'LEASH'].includes(item.product.type)) {
              item.product.quantity = 1;
              item.product.totalQtyPrice = Number(item.product.price);
              item.product.totalQtyDiscountedPrice = Number(item.subtotal - item.discount);
            }
            sections.miscellaneous.push(item);
          } else {
            let existingItemIndex = sections.miscellaneous.findIndex((prod) => prod.product.id === item.product.id);
            if (existingItemIndex !== -1) {
              sections.miscellaneous[existingItemIndex].product.quantity++;
              sections.miscellaneous[existingItemIndex].product.totalQtyPrice += item.product.price;
              sections.miscellaneous[existingItemIndex].product.totalQtyDiscountedPrice += Number(item.subtotal - item.discount);
            } else {
              item.product.quantity = 1;
              item.product.totalQtyPrice = item.product.price;
              item.product.totalQtyDiscountedPrice = Number(item.subtotal - item.discount);
              sections.miscellaneous.push(item);
            }
          }
        }
      });
    }
    return sections;
  },
  licensedPetsWithoutTag: (state) => {
    const petsWithLicense = {};
    state.order.items?.forEach((item) => {
      if (item.product?.type === 'LICENCE') {
        petsWithLicense[item.group?.pet?.id] = null;
      }
    });
    state.order.items?.forEach((item) => {
      if (item.parent) {
        return;
      }
      if (['DESIGNER_TAG', 'PET_TAG'].includes(item.product?.type)) {
        if (item.group?.pet?.id) delete petsWithLicense[item.group?.pet?.id];
      }
    });
    return Object.keys(petsWithLicense);
  },
  // it finds unassigned pet product and returns false if so
  allProductsAssigned: (state) => {
    return !state.order.items?.some((item) => {
      if (item.parent) {
        return false;
      }
      if (['DESIGNER_TAG', 'PET_TAG'].includes(item.product?.type)) {
        return !item.group?.pet;
      } else if (['COLLAR'].includes(item.product?.type)) {
        return !item.group?.pet && item.customTagOrderInfo?.phone;
      }
    });
  },
  // Check if all pets with licenses have a tag assigned and/or tags should have a pet assigned
  isValid: (state, getters) => {
    return getters.allProductsAssigned && getters.licensedPetsWithoutTag.length === 0;
  },
  renewalPetsInOrder: (state, getters) =>
    state.order.petOwner.renewalPets.filter((renewalPet) => getters.getSections?.pets[renewalPet?.id]?.licenses.length == 0),
  nextAccountPetWithoutTag: (state, getters) => (currentPetId) => {
    const accountPets = state.order?.petOwner?.pets;
    if (!accountPets?.length) return null;

    return accountPets.find((pet) => {
      if (String(pet.id) === String(currentPetId)) return false;
      const ownsTag = Array.isArray(pet.tags) && pet.tags.length > 0;
      const hasTagInBasket = (getters.getSections?.pets?.[pet.id]?.tags?.length ?? 0) > 0;
      return !ownsTag && !hasTagInBasket;
    }) ?? null;
  },
  hasPromoCode: (state) => !!state.order?.discount?.code,
  showsPlusSavingsBanner: (state, getters, rootState, rootGetters) => (petId) => {
    if (!rootGetters.automatedDiscountsEnabled || getters.hasPromoCode) return false;

    return (getters.getSections?.pets?.[petId]?.subscriptions?.length ?? 0) > 0;
  },
};
